<template>
    <plant-page
        v-if="currentCountry === 'Gb'"
        v-model:visible="isVisible"
        :plant-initial-ids="[props.plantId]"
        :fromDate="props.fromDate"
        :toDate="props.toDate"
        :initial-details-tab="props.initialDetailsTab"
        @hide="emit('hide')"
    />
    <central-modal
        v-else
        v-model:visible="isVisible"
        header="Plant Viewer"
        hide-header
        size="large"
        @hide="emit('hide')"
    >
        <template #header>
            <div class="plant-page-header-options">
                <override-dashboard-dates
                    has-generic-charts
                    :date-overrides="explicitPlantDateOverride"
                    @update:current-dates-override="updateDashboardDatesOverride"
                />
            </div>
        </template>
        <component
            v-if="isDefinedCountry"
            :is="definedCountryComponent"
            :plant-id="props.plantId"
            :fromDate="props.fromDate"
            :toDate="props.toDate"
            :date-override="explicitPlantDateOverride"
        />
        <europe-plant-page
            v-else
            :plant-id="props.plantId"
            :fromDate="props.fromDate"
            :toDate="props.toDate"
            :country="currentCountry"
            :date-override="explicitPlantDateOverride"
        />
    </central-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import PlantPage from '@/components/powerMarkets/plant/PlantPage.vue';
import IrelandPlantPage from '@/components/powerMarkets/plant/IrelandPlantPage.vue';
import FrancePlantPage from '@/components/powerMarkets/plant/FrancePlantPage.vue';
import EuropePlantPage from '@/components/powerMarkets/plant/EuropePlantPage.vue';
import OverrideDashboardDates from '@/components/powerMarkets/dashboard/OverrideDashboardDates.vue';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import CentralModal from '@/components/common/CentralModal.vue';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import { usePlantDateOverride } from '@/composables/powerMarkets/usePlantDateOverride.ts';

const dataStore = useDataStore();

const props = defineProps<{
    visible: boolean;
    plantId: string;
    fromDate?: Date;
    toDate?: Date;
    initialDetailsTab?: PlantDetailsTab;
}>();

const emit = defineEmits<{
    'update:visible': [boolean];
    'hide': [];
}>();

const isVisible = ref<boolean>(props.visible);

const {
    explicitPlantDateOverride,
    updateDashboardDatesOverride,
    resetPlantDateOverride,
} = usePlantDateOverride();

const currentCountry = computed<string>(() => {
    let countryId = 'Gb';

    const plantsByEndpoint = Object.values(dataStore.plantsByEndpoint);
    Object.values(plantsByEndpoint).forEach((plants) => {
        Object.keys(plants.plantList ?? {}).forEach((country) => {
            plants.plantList[country].forEach((plant) => {
                if (plant.id === props.plantId) {
                    countryId = country;
                }
            });
        });
    });
    return countryId;
});

const isDefinedCountry = computed<boolean>(() => ['Ireland', 'France'].includes(currentCountry.value));

const definedCountryComponent = computed(() => {
    switch (currentCountry.value) {
        case 'Ireland':
            return IrelandPlantPage;
        case 'France':
            return FrancePlantPage;
        default:
            return null;
    }
});

watch(() => props.visible, (newVal) => {
    isVisible.value = newVal;
    if (!newVal) {
        resetPlantDateOverride();
    }
});

watch(() => props.plantId, () => {
    resetPlantDateOverride();
});

watch(isVisible, (newVal) => {
    emit('update:visible', newVal);
    if (!newVal) {
        resetPlantDateOverride();
    }
});
</script>

<style lang="scss" scoped>
.plant-page-header-options {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
    padding: 0 var(--largeSpacing);
    justify-self: end;

    button {
        flex-shrink: 0;
    }
}
</style>
